import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from 'extended-oxygen-elements';
import { SiteDutyConfig } from '../types';
import { DutyRow } from './DutyRow';
import { getDutyIcon } from '../utils/iconUtils';
import * as TableStyles from '../styles/tableStyles';

type DutiesTableProps = {
  configs: SiteDutyConfig[];
  isLoading: boolean;
  onConfigure: (dutyType: string) => void;
};

export function DutiesTable({ configs, isLoading, onConfigure }: DutiesTableProps) {
  const dutiesWithIcons = useMemo(
    () => configs.map(config => ({ config, icon: getDutyIcon(config.duty.key) })),
    [configs]
  );

  if (isLoading) {
    return (
      <Box
        sx={TableStyles.loadingContainerStyles}
        role="status"
        aria-live="polite"
      >
        <CircularProgress aria-label="Loading duties" />
      </Box>
    );
  }

  if (!configs || configs.length === 0) {
    return (
      <Box
        sx={TableStyles.emptyStateContainerStyles}
        role="status"
        aria-live="polite"
      >
        <Typography variant="body1" color="textSecondary">
          No duty configurations found for this location and day.
        </Typography>
      </Box>
    );
  }

  return (
    <Box role="table" aria-label="Duties configuration table">
      {/* Desktop Header - Hidden on Mobile */}
      <Box role="rowgroup" sx={TableStyles.tableHeaderRowStyles}>
        <Box role="columnheader" sx={TableStyles.dutyColumnHeaderStyles}>
          <Typography variant="body2" sx={TableStyles.columnHeaderTypographyStyles}>
            Duty
          </Typography>
        </Box>
        <Box role="columnheader" sx={TableStyles.timesColumnHeaderStyles}>
          <Typography variant="body2" sx={TableStyles.columnHeaderTypographyStyles}>
            Times Required
          </Typography>
        </Box>
        <Box role="columnheader" sx={TableStyles.peopleColumnHeaderStyles}>
          <Typography variant="body2" sx={TableStyles.columnHeaderTypographyStyles}>
            No. People
          </Typography>
        </Box>
        <Box role="columnheader" sx={TableStyles.configureColumnHeaderStyles}>
          <Typography variant="body2" sx={TableStyles.columnHeaderTypographyStyles}>
            Configure
          </Typography>
        </Box>
      </Box>

      {/* Rows */}
      <Box role="rowgroup">
        {dutiesWithIcons.map(({ config, icon }, index) => (
          <React.Fragment key={config.duty.id}>
            <DutyRow
              config={config}
              onConfigure={onConfigure}
              icon={icon}
            />
            {/* Desktop divider - only between rows */}
            {index < dutiesWithIcons.length - 1 && (
              <Box sx={TableStyles.rowDividerStyles} aria-hidden="true" />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
